import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { CardAuthorMark } from './AuthorMark';
import { QuickAddButton } from './QuickAdd';
import { IPremadeQuiz, RoutePath } from '@quizzach/interfaces';
import { SaleCard } from '@quizzach/styles';
import { premadeLevelKey } from '@quizzach/utils';

/**
 * Premade quiz card as a sale ticket — the same panorama ticket the profile
 * shelf shows for owned quizzes, before purchase: the coupon carries the
 * price and the author's signature where the owned card carries its barcode.
 * The card is a real link to the detail page (so middle-click and »open in
 * new tab« work); the corner circle quick-adds to cart (guests get the auth
 * modal) or, when owned, leads to the profile collection.
 *
 * `hideMeta` drops the whole meta row (difficulty and question count) and
 * `hideQuickAdd` the corner circle — the homepage marquee shows the ticket
 * with the title alone and no cart control (so it needs no ownership
 * provider); the whole card just leads to the detail page.
 */
export const PremadeQuizCardComponent = ({
	quiz,
	hideMeta,
	hideQuickAdd,
}: {
	quiz: IPremadeQuiz;
	hideMeta?: boolean;
	hideQuickAdd?: boolean;
}) => {
	const { t } = useTranslation();
	const levelKey = premadeLevelKey(quiz.level);
	const levelLabel = levelKey ? t(levelKey) : '';

	return (
		<SaleCard
			as={Link}
			to={`${RoutePath.PremadeQuizzes}/quiz/${quiz.uuid}`}
			data-id='premade-quiz-card'
			onClick={(e: MouseEvent<HTMLAnchorElement>) => {
				// clicks inside the portaled auth modal bubble through the React
				// tree to this link — they must not navigate away
				if (!e.currentTarget.contains(e.target as Node)) e.preventDefault();
			}}
		>
			<div className='ph'>
				<img
					className='bg'
					src={`${import.meta.env.VITE_ASSETS_URL}/thematic_quizzes/${
						quiz.cover_photo_small
					}`}
					alt={quiz.title}
					loading='lazy'
					data-darkzone
				/>
				{!hideQuickAdd && <QuickAddButton quiz={quiz} />}
			</div>
			<div className='tt'>
				<h3>{quiz.title}</h3>
				{!hideMeta && (
					<span className='im'>
						{levelLabel && `${levelLabel} · `}
						{t('premade.card.questionCount', {
							count: quiz.number_of_questions - 1,
						})}
					</span>
				)}
			</div>
			<div className='holes' aria-hidden='true' />
			<div className='stub'>
				<div className='pr'>
					<span className='lb'>{t('premade.card.priceLabel')}</span>
					<span className='eur'>
						{quiz.price?.replace('.00', '').replace('.', ',')} EUR
					</span>
				</div>
				<span className='by'>
					<CardAuthorMark displayName={quiz.author?.display_name} />
				</span>
			</div>
		</SaleCard>
	);
};
